import React, { FunctionComponent, useEffect, useState } from "react";
import { useAppSelector } from "../../state/store";
import {
  BlockContainer,
  IconContainer,
  SideModalStyles,
  BlockTipPopoverContainer,
} from "./style";
import { Beta } from "../header-component/headerComponent";
import {
  AdBanner,
  BannerThirdPartyIcon,
  BannerType,
  Tooltip,
  TitleSmall,
  BodyCaption,
} from "@bikdotai/bik-component-library";
import { NodeSubType } from "../../models/nodeSubType";
import { useVersionJet } from "../../utilities/useVersionJet";

export interface SubSections {
  subHeader: string;
  listOfBlocks: {
    blockName: string;
    blockIcon: any;
    blockId: string;
    automationChannel?: string;
    nodeType?: string;
    isBeta?: boolean;
    description?: string;
    releaseFeature?: string;
    blockTip?: {
      title: string;
      body?: string;
      steps: string[];
    };
  }[];
}

export interface SideModalProps {
  storeId: string;
  header: string;
  caption: string;
  subSections: SubSections[];
  setNewNodeData: (
    blockId: string,
    blockName: string,
    blockChannel?: string,
    blockNodeType?: string
  ) => void;
  onClick?: () => void;
  closeModal?: () => void;
  isOpen: boolean;
  isManifestStore?: boolean;
  isBikIntegrated?: boolean;
  feature?: string | null;
}
type Props = SideModalProps;

export interface SubBlockHeaderProps {
  subBlockName: string;
}

const SubBlockHeader: React.FC<SubBlockHeaderProps> = (props) => {
  return (
    <div className="section__header-container">
      <div className="section__header">{props.subBlockName}</div>
      <div className="section__bar" />
    </div>
  );
};

export interface BlockProps {
  channel: string;
  blockName: string;
  blockIcon: any;
  blockId: string;
  blockChannel?: string;
  blockNodeType?: string;
  click: (
    blockId: string,
    blockName: string,
    blockChannel: string,
    blockNodeType?: string
  ) => void;
  isBeta?: boolean;
  isHighlighted?: boolean;
}

const ONLY_SSO_BLOCKS = [
  NodeSubType.STORE_VARIABLE,
  NodeSubType.MANIFEST_SEARCH,
  NodeSubType.MANIFEST_LLM_CALL,
  NodeSubType.MANIFEST_EXIT,
  NodeSubType.MANIFEST_BUILD_LLM_INPUT,
  NodeSubType.CALL_API,
  NodeSubType.EVALUATE,
  NodeSubType.AI_AGENT,
  NodeSubType.AGENTIC_BUILDER,
];

// The agentic builder block replaces the legacy AI agent block and is released
// under the legacy block's existing plan flag rather than a new one.
const PLAN_FEATURE_KEY: Partial<Record<string, string>> = {
  [NodeSubType.AGENTIC_BUILDER]: NodeSubType.AI_AGENT,
};
const planFeatureKey = (blockId: string) => PLAN_FEATURE_KEY[blockId] ?? blockId;

const Block: React.FC<BlockProps> = (props) => {
  const {
    blockName,
    blockIcon,
    blockId,
    blockChannel,
    blockNodeType,
    click,
    channel,
    isHighlighted,
  } = props;
  const blockRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    if (isHighlighted && blockRef.current) {
      blockRef.current.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    }
  }, [isHighlighted]);

  return (
    <BlockContainer
      ref={blockRef}
      channel={channel}
      subType={blockId}
      onClick={() =>
        click(blockId, blockName, blockChannel || "", blockNodeType)
      }
      isHighlighted={isHighlighted}
    >
      {props.isBeta && (
        <div
          style={{
            position: "absolute",
            top: -10,
            left: -10,
          }}
        >
          <Beta />
        </div>
      )}
      <IconContainer channel={channel} subType={blockId}>
        <div className="d-flex m-auto">{blockIcon}</div>
      </IconContainer>
      <div className="block__caption">{blockName}</div>
    </BlockContainer>
  );
};

interface BlockTipTooltipProps {
  title: string;
  body?: string;
  steps: string[];
  isHighlighted?: boolean;
  isPanelOpen: boolean;
  children: React.ReactNode;
  flowName?: string;
}

const BlockTipTooltip: React.FC<BlockTipTooltipProps> = ({
  title,
  body,
  steps,
  isHighlighted,
  isPanelOpen,
  children,
  flowName = "",
}) => {
  const resolveStep = (step: string) =>
    step.replace(/\{\{flowName\}\}/g, flowName || "this journey");
  const [dismissed, setDismissed] = useState(false);
  const [isPopoverReady, setIsPopoverReady] = useState(false);

  useEffect(() => {
    setIsPopoverReady(false);

    if (!isPanelOpen || !isHighlighted || dismissed) {
      return;
    }

    // SideModal slide-in animation is 0.5s; wait for it to finish so tooltip positioning
    // is computed when the panel is at its final transform.
    const timeoutId = window.setTimeout(() => {
      setIsPopoverReady(true);
    }, 550);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [isPanelOpen, isHighlighted, dismissed]);

  const showPopover =
    isPanelOpen && isHighlighted && !dismissed && isPopoverReady;

  const tooltipContent = (
    <BlockTipPopoverContainer>
      <TitleSmall style={{ color: "#FFFFFF" }}>{title}</TitleSmall>
      {body && (
        <BodyCaption style={{ color: "#FFFFFF" }}>
          {resolveStep(body)}
        </BodyCaption>
      )}
      <ol>
        {steps.map((step, i) => (
          <li key={i}>
            <BodyCaption style={{ color: "#FFFFFF" }}>
              {resolveStep(step)}
            </BodyCaption>
          </li>
        ))}
      </ol>
      <TitleSmall
        style={{ color: "#FFFFFF", cursor: "pointer", marginTop: 4 }}
        onClick={() => setDismissed(true)}
      >
        Understood
      </TitleSmall>
    </BlockTipPopoverContainer>
  );

  return (
    <Tooltip
      placement="left"
      tooltipContent={tooltipContent}
      alwaysVisible={showPopover}
    >
      <div>{children}</div>
    </Tooltip>
  );
};

const SideModal: FunctionComponent<Props> = (props) => {
  const homeState = useAppSelector((state) => state.homeState);
  const [isSSO, setIsSSO] = useState<boolean>(false);
  const { isVersionApplicabletoPlan } = useVersionJet();

  useEffect(() => {
    if (homeState.userData && homeState.userData?.loginType === "sso") {
      setIsSSO(true);
    }
  }, [homeState?.flowId]);

  const onBlockClick = (
    blockId: string,
    blockName: string,
    blockChannel?: string,
    blockNodeType?: string
  ) => {
    props.setNewNodeData(blockId, blockName, blockChannel, blockNodeType);
  };

  return (
    <>
      <SideModalStyles isOpen={props.isOpen}>
        <div className="sticky-top">
          <div className="header">{props.header}</div>
          <div className="caption">{props.caption}</div>
        </div>
        {props.subSections &&
          props.subSections.map((section) =>
            section.subHeader == "Manifest blocks" && !isSSO ? null : (
              <div className="section">
                <SubBlockHeader subBlockName={section.subHeader} />
                {
                  <div className="block__outer-container">
                    {section.listOfBlocks.map((block) =>
                      (ONLY_SSO_BLOCKS.includes(block.blockId as any) &&
                      !isSSO &&
                      !isVersionApplicabletoPlan(planFeatureKey(block.blockId))) ||
                      (block.releaseFeature &&
                      !isVersionApplicabletoPlan(block.releaseFeature)) ? null : (
                        <>
                          {block.blockTip ? (
                            <BlockTipTooltip
                              title={block.blockTip.title}
                              body={block.blockTip.body}
                              steps={block.blockTip.steps}
                              isHighlighted={props.feature === block.blockId}
                              isPanelOpen={props.isOpen}
                              flowName={homeState.flowName}
                            >
                              <Block
                                channel={homeState.channel || "whatsapp"}
                                blockName={block.blockName}
                                blockIcon={block.blockIcon}
                                blockId={block.blockId}
                                blockChannel={block?.automationChannel}
                                click={onBlockClick}
                                isBeta={block?.isBeta || false}
                                isHighlighted={props.feature === block.blockId}
                              />
                            </BlockTipTooltip>
                          ) : (
                            <Tooltip
                              delay={[400, 0] as unknown as number}
                              body={block.description || ""}
                              placement="bottom"
                            >
                              <div>
                                <Block
                                  channel={homeState.channel || "whatsapp"}
                                  blockName={block.blockName}
                                  blockIcon={block.blockIcon}
                                  blockId={block.blockId}
                                  blockChannel={block?.automationChannel}
                                  blockNodeType={block?.nodeType}
                                  click={onBlockClick}
                                  isBeta={block?.isBeta || false}
                                  isHighlighted={
                                    props.feature === block.blockId
                                  }
                                />
                              </div>
                            </Tooltip>
                          )}
                        </>
                      )
                    )}
                  </div>
                }
              </div>
            )
          )}
        {homeState.channel === "livechat" &&
          props.header.includes("Message block") &&
          !!props?.isManifestStore &&
          !props?.isBikIntegrated && (
            <div style={{ paddingRight: "16px" }}>
              <AdBanner
                containerStyle={{
                  background: "#EFE6F933",
                  padding: "16px",
                  gap: "8px",
                  borderRadius: "8px",
                }}
                bannerType={BannerType.CARD_BANNER}
                title="Unlock more with BIK"
                subTitle="Leverage Manifest's capabilities across various channels like WhatsApp, Instagram, email, and more using BIK."
                icons={[
                  BannerThirdPartyIcon.WHATSAPP,
                  BannerThirdPartyIcon.INSTAGRAM,
                  BannerThirdPartyIcon.EMAIL,
                ]}
                buttonText="Explore BIK"
                onClick={() => window.open("https://bik.ai")}
                buttonSecondaryText="Book a demo"
                onClickSecondary={() =>
                  window.open("https://calendly.com/sonakshin/30min")
                }
              />
            </div>
          )}
      </SideModalStyles>
    </>
  );
};
export default SideModal;
